import type { TypedDocumentNode } from '@apollo/client'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import SFBackPageHeader from '@web/src/components/primatives/sf-back-page-header/sf-back-page-header'
import RendezvousForm from '@web/src/features/rendezvous/rendezvous-form/rendezvous-form'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type { CreateRendezvousInput, CreateRendezvousMutation, CreateRendezvousMutationVariables } from 'types/graphql'

const CREATE_RENDEZVOUS_MUTATION: TypedDocumentNode<CreateRendezvousMutation, CreateRendezvousMutationVariables> = gql`
  mutation CreateRendezvousMutation($input: CreateRendezvousInput!) {
    createRendezvous(input: $input) {
      id
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/rendezvous/create')({
  component: CreateRendezvousPage,
  head: () =>
    seo({
      title: `Rendezvous Create | ${env.VITE_APP_NAME}`,
      description: 'Create a new rendezvous event. Invite your friends to join you for a fun night out.',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
})

function CreateRendezvousPage() {
  const { t } = useTranslation()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const navigate = useNavigate()
  const router = useRouter()

  const [createRendezvous] = useMutation(CREATE_RENDEZVOUS_MUTATION, {
    onCompleted: () => {
      toast.success(t('Rendezvous created successfully!'))
      navigate({ to: '/rendezvous' })
      setShowSpinnerValue(false)
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
  })

  const onSave = (input: CreateRendezvousInput) => {
    setShowSpinnerValue(true)
    createRendezvous({ variables: { input } })
  }

  const handleCancel = () => {
    router.history.back()
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pt-4">
      <div className="flex w-full flex-col items-stretch gap-4">
        <SFBackPageHeader
          lowerText={t('Set up your new rendezvous details')}
          onBackClick={handleCancel}
          title={t('Create Rendezvous')}
        />
        <RendezvousForm onSave={onSave} showSpinner={showSpinner} />
      </div>
    </div>
  )
}
